// /admin/site: things customers see that can be changed without a developer:
// the banner for free members (a computer and a phone image) and the welcome video.
import { useEffect, useState } from 'react'

import { deleteAd, getAd, getVideo, saveAd, saveVideo } from '../../../api/adminV2'
import { FIELD_ADVERTISING, LIMIT_SIZE_IMAGE, URL_PATTERN } from '../../../utils/constants'
import { LoadError, Loading, useFeedback, useLoad } from '../ui'

const SIZES = FIELD_ADVERTISING.map((f) => ({
  isDesktop: f.is_desktop,
  title: f.is_desktop ? 'Computer size' : 'Phone size',
  size: f.textWidth.replace('x', ' × '),
  inputId: `adm-${f.idName}`,
}))

const megabytes = (bytes) => `${(bytes / 1024 / 1024).toFixed(bytes > 10485760 ? 0 : 1)} MB`

const loadAds = () =>
  Promise.all([getAd(true), getAd(false)]).then(([desktop, mobile]) => ({
    desktop: desktop || null,
    mobile: mobile || null,
  }))

const Banner = () => {
  const { toast, toastError, confirm } = useFeedback()
  const { data, error, loading, reload } = useLoad(loadAds, [])
  const [link, setLink] = useState('')
  const [files, setFiles] = useState({})
  const [previews, setPreviews] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (data) setLink(data.desktop?.link || data.mobile?.link || '')
  }, [data])

  if (error && !data) return <LoadError error={error} onRetry={reload} />
  if (loading && !data) return <Loading />

  const current = (isDesktop) => (isDesktop ? data?.desktop : data?.mobile)
  const hasAny = !!(data?.desktop || data?.mobile)

  const choose = (isDesktop, file) => {
    if (!file) return
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      toastError('Choose a PNG or JPG image.')
      return
    }
    if (file.size > LIMIT_SIZE_IMAGE) {
      toastError(`That image is ${megabytes(file.size)}. The limit is 15 MB.`)
      return
    }
    setFiles((f) => ({ ...f, [isDesktop]: file }))
    setPreviews((p) => ({ ...p, [isDesktop]: URL.createObjectURL(file) }))
  }

  const save = () => {
    const url = link.trim()
    if (!URL_PATTERN.test(url)) {
      toastError('Enter the full web address the banner links to, starting with https://')
      return
    }
    const jobs = SIZES.filter((s) => files[s.isDesktop] || current(s.isDesktop))
    if (!jobs.length) {
      toastError('Choose at least one image first.')
      return
    }
    setSaving(true)
    // Only send an image when a new one was chosen; sending none keeps the current image
    Promise.all(
      jobs.map((s) =>
        saveAd({
          is_desktop: s.isDesktop,
          link: url,
          ...(files[s.isDesktop] ? { image: files[s.isDesktop] } : {}),
        })
      )
    )
      .then(() => {
        toast('Banner saved')
        setFiles({})
        setPreviews({})
        reload()
      })
      .catch((e) => {
        toastError(e, 'The banner did not save. Please try again.')
        reload()
      })
      .finally(() => setSaving(false))
  }

  const remove = () =>
    confirm({
      title: 'Remove the banner?',
      text: 'Free members stop seeing it on computers and phones. You can add a new one any time.',
      action: 'Remove banner',
      danger: true,
      run: () => Promise.all([deleteAd(true), deleteAd(false)]),
    }).then((ok) => {
      if (!ok) return
      toast('Banner removed')
      setFiles({})
      setPreviews({})
      setLink('')
      reload()
    })

  return (
    <section className="a-panel">
      <h2>Banner shown to free members</h2>
      <p className="a-muted">Appears above family groups for people on the free plan.</p>
      {SIZES.map((s) => {
        const preview = previews[s.isDesktop] || current(s.isDesktop)?.image
        return (
          <div className="a-upload" key={String(s.isDesktop)}>
            <b style={{ color: 'var(--ink)' }}>{s.title}</b>
            {preview ? <img src={preview} alt={`${s.title} banner`} /> : null}
            <span>
              {s.size} image
              {files[s.isDesktop] ? ` · new: ${files[s.isDesktop].name} (not saved yet)` : ''}
              {!preview ? ' · none yet' : ''}
            </span>
            <input
              id={s.inputId}
              className="a-file"
              type="file"
              accept=".png, .jpg, .jpeg"
              onChange={(e) => {
                choose(s.isDesktop, e.target.files?.[0])
                e.target.value = ''
              }}
            />
            <label htmlFor={s.inputId} className="a-btn a-small">
              {preview ? 'Replace image' : 'Choose image'}
            </label>
          </div>
        )
      })}
      <label className="a-field-label" htmlFor="adm-adlink">
        Where it links to
      </label>
      <input
        id="adm-adlink"
        className="a-input"
        type="url"
        placeholder="https://recipes.getmorsel.com/settings"
        value={link}
        onChange={(e) => setLink(e.target.value)}
      />
      <div className="a-btn-row">
        <button
          type="button"
          className="a-btn a-primary"
          onClick={save}
          disabled={saving}
          aria-disabled={saving}
        >
          {saving ? 'Saving…' : 'Save banner'}
        </button>
        {hasAny ? (
          <button type="button" className="a-btn" onClick={remove}>
            Remove banner
          </button>
        ) : null}
      </div>
    </section>
  )
}

const Video = () => {
  const { toast, toastError } = useFeedback()
  const { data, error, loading, reload } = useLoad(getVideo, [])
  const [title, setTitle] = useState('')
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (data) setTitle(data.text || '')
  }, [data])

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview])

  if (error && !data) return <LoadError error={error} onRetry={reload} />
  if (loading && !data) return <Loading />

  const changed = !!file || title.trim() !== (data?.text || '')

  const save = () => {
    if (!changed) {
      toastError('Nothing has changed yet.')
      return
    }
    if (!title.trim() && !file) {
      toastError('Enter a title for the video.')
      return
    }
    setSaving(true)
    saveVideo({ ...(title.trim() ? { text: title.trim() } : {}), ...(file ? { video: file } : {}) })
      .then(() => {
        toast(file ? 'Video saved' : 'Title saved')
        setFile(null)
        setPreview(null)
        reload()
      })
      .catch((e) => toastError(e, 'The video did not save. Please try again.'))
      .finally(() => setSaving(false))
  }

  const src = preview || data?.url

  return (
    <section className="a-panel">
      <h2>Welcome video on the profile page</h2>
      <label className="a-field-label" htmlFor="adm-vidtitle">
        Title shown above the video
      </label>
      <input
        id="adm-vidtitle"
        className="a-input"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="a-upload">
        {src ? <video src={src} controls playsInline preload="metadata" /> : null}
        <span>
          {file
            ? `${file.name} · ${megabytes(file.size)} (not saved yet)`
            : src
            ? 'Current video'
            : 'No video yet'}
        </span>
        <input
          id="adm-video"
          className="a-file"
          type="file"
          accept=".mp4, .mov, .webm, .m4v"
          onChange={(e) => {
            const chosen = e.target.files?.[0]
            if (chosen) {
              setFile(chosen)
              setPreview(URL.createObjectURL(chosen))
            }
            e.target.value = ''
          }}
        />
        <label htmlFor="adm-video" className="a-btn a-small">
          {src ? 'Replace video' : 'Choose video'}
        </label>
      </div>
      <div>
        <button
          type="button"
          className="a-btn a-primary"
          onClick={save}
          disabled={saving}
          aria-disabled={saving}
        >
          {saving ? (file ? 'Uploading…' : 'Saving…') : 'Save'}
        </button>
      </div>
    </section>
  )
}

const SiteContent = () => (
  <>
    <div className="a-head">
      <div>
        <h1>Site content</h1>
        <p className="a-sub">Things customers see that you can change without a developer.</p>
      </div>
    </div>
    <div className="a-grid2">
      <Banner />
      <Video />
    </div>
  </>
)

export default SiteContent
